/* ============================================================
   PiecewiseConfigurator — Proposal 5B (2026-04-25)

   The unified Piecewise editor. Replaces the four hardcoded Settings
   children with one configurable model-schedule surface.
   with a single screen where users:

     1. Pick a starter template — seeds reasonable labels for their
        enterprise model stack.
     2. Inline-rename each of the four built-in engine kinds.
     3. Edit the schedules per kind (existing PiecewiseEditor + PinsEditor
        components are reused — engine internals unchanged).

   The four engine kinds — familyAp, subs, fixedAssets, ltl — are still
   the only pathways the engine knows. Users only re-label them. Phase 2
   of this proposal generalizes the engine to honor user-added kinds.
   ============================================================ */
import React, { useState } from 'react';
import PiecewiseEditor from './PiecewiseEditor.jsx';
import PinsEditor from './PinsEditor.jsx';
import { useAppConfirm } from './AppDialog.jsx';
import {
  PIECEWISE_STARTER_TEMPLATES,
  DEFAULT_PIECEWISE_LABELS,
  DEFAULT_PIECEWISE_HINTS,
} from '../content/piecewise-starters.js';
import {
  recurringKey, FAMILY_DIRECT_KEY, MARIAN_KEY,
  resolveSchedule, fmtMoney,
} from '../engine.js';
import { RECURRING, FAMILY_AP, FIXED_ASSETS, LONG_TERM_LIABILITIES } from '../data.js';

const SECTION = 'rounded-[8px] border border-border px-4 py-[14px] bg-white/[0.02] mb-4';
const SECTION_HEAD = 'flex items-baseline justify-between gap-3 flex-wrap mb-[10px]';
const LABEL_INPUT = 'bg-surface-raised text-text border border-border rounded-sm px-[10px] py-[5px] font-[inherit] text-[0.92rem] font-semibold w-[260px] focus:outline-none focus:border-accent-blue';
const HINT_INPUT = 'bg-surface-raised text-text-muted border border-dashed border-border rounded-sm px-[10px] py-[4px] font-[inherit] text-[0.78rem] w-full mt-[6px] focus:outline-none focus:border-accent-blue placeholder:italic placeholder:text-text-dim';
const HINT = 'mt-[10px] text-[0.78rem] text-text-muted px-3 py-[0.5rem] bg-surface-el border-l-[3px] border-border-strong rounded-sm';
const STARTER_BAR = 'flex flex-wrap gap-2 items-center mb-4 px-[14px] py-[10px] border border-dashed border-border rounded-[8px] bg-surface';
const STARTER_BTN = 'bg-transparent text-text border border-border-strong px-[12px] py-[6px] rounded-sm font-[inherit] text-[0.82rem] cursor-pointer hover:border-accent-blue hover:text-accent-blue transition-colors';
const STARTER_BTN_ACTIVE = '!bg-accent-blue !text-white !border-accent-blue';
const RESET_BTN = 'bg-transparent text-text-muted border border-dashed border-border-strong px-[10px] py-[5px] rounded-sm font-[inherit] text-[0.76rem] cursor-pointer hover:text-accent-red hover:border-accent-red';

const KINDS = [
  {
    id: 'familyAp',
    description:
      'Two-channel AP cadence (direct + routed by default). Engine reads piecewiseOverrides keyed by FAMILY_DIRECT_KEY and MARIAN_KEY.',
    renderBody: ({ scenario, setOverride }) => {
      const overrides = scenario.piecewiseOverrides || {};
      return (
        <>
          <PiecewiseEditor
            label="Direct channel default"
            defaultMonthly={FAMILY_AP.direct.monthlyAmount}
            schedule={overrides[FAMILY_DIRECT_KEY] ?? null}
            onChange={(next) => setOverride(FAMILY_DIRECT_KEY, next)}
            expectedTotalLabel={fmtMoney(FAMILY_AP.direct.monthlyAmount, { decimals: 0 })}
          />
          <PiecewiseEditor
            label="Routed channel default"
            defaultMonthly={FAMILY_AP.viaMarian.monthlyAmount}
            schedule={overrides[MARIAN_KEY] ?? null}
            onChange={(next) => setOverride(MARIAN_KEY, next)}
            expectedTotalLabel={fmtMoney(FAMILY_AP.viaMarian.monthlyAmount, { decimals: 0 })}
          />
        </>
      );
    },
  },
  {
    id: 'subs',
    description:
      'Recurring monthly / yearly / quarterly. Engine reads piecewiseOverrides keyed by recurringKey(line).',
    renderBody: ({ scenario, setOverride }) => {
      const overrides = scenario.piecewiseOverrides || {};
      const renderRow = (r) => (
        <PiecewiseEditor
          key={recurringKey(r)}
          label={`${r.code} · ${r.vendor}`}
          defaultMonthly={r.amount}
          schedule={overrides[recurringKey(r)] ?? null}
          onChange={(next) => setOverride(recurringKey(r), next)}
          expectedTotalLabel={fmtMoney(r.amount, { decimals: 0 })}
        />
      );
      return (
        <>
          <p className="text-[0.78rem] text-text-muted m-0 mb-[6px]">
            <strong className="text-text">Monthly ({RECURRING.monthly.length})</strong>
          </p>
          {RECURRING.monthly.map(renderRow)}
          <p className="text-[0.78rem] text-text-muted mt-3 mb-[6px]">
            <strong className="text-text">Yearly ({RECURRING.yearly.length})</strong>
          </p>
          {RECURRING.yearly.map(renderRow)}
          <p className="text-[0.78rem] text-text-muted mt-3 mb-[6px]">
            <strong className="text-text">Quarterly ({RECURRING.quarterly.length})</strong>
          </p>
          {RECURRING.quarterly.map(renderRow)}
        </>
      );
    },
  },
  {
    id: 'fixedAssets',
    description:
      'Per-asset value pins per month. Engine reads piecewiseAssets[assetId] for valuation overrides across the forecast horizon.',
    renderBody: ({ scenario, setScenario }) => {
      const setAssetPin = (assetId, next) => {
        setScenario({
          ...scenario,
          piecewiseAssets: { ...(scenario.piecewiseAssets ?? {}), [assetId]: next },
        });
      };
      return FIXED_ASSETS
        .filter((a) => a.status !== 'not-yet-acquired')
        .map((asset) => (
          <PinsEditor
            key={asset.id}
            label={`${asset.name} · ${asset.category}`}
            defaultValue={asset.currentValue ?? 0}
            pins={scenario.piecewiseAssets?.[asset.id] ?? null}
            onChange={(next) => setAssetPin(asset.id, next)}
            valueKey="value"
            valueLabel="Current value"
            hintText={asset.note ? `Baseline note: ${asset.note}` : undefined}
          />
        ));
    },
  },
  {
    id: 'ltl',
    description:
      'Outstanding-balance pins per month for long-term liabilities. Engine reads piecewiseLTLs[ltlId] for amortization overrides.',
    renderBody: ({ scenario, setScenario }) => {
      const setLTLPin = (ltlId, next) => {
        setScenario({
          ...scenario,
          piecewiseLTLs: { ...(scenario.piecewiseLTLs ?? {}), [ltlId]: next },
        });
      };
      return LONG_TERM_LIABILITIES.map((ltl) => (
        <PinsEditor
          key={ltl.id}
          label={ltl.name}
          defaultValue={ltl.outstanding ?? 0}
          pins={scenario.piecewiseLTLs?.[ltl.id] ?? null}
          onChange={(next) => setLTLPin(ltl.id, next)}
          valueKey="outstanding"
          valueLabel="Outstanding"
          hintText={ltl.note ? `Baseline note: ${ltl.note}` : undefined}
        />
      ));
    },
  },
];

export default function PiecewiseConfigurator({ scenario, setScenario }) {
  /* Labels live on scenario.piecewiseLabels so they save with profiles. */
  const labels = scenario.piecewiseLabels ?? DEFAULT_PIECEWISE_LABELS;
  const hints = DEFAULT_PIECEWISE_HINTS;
  const [expanded, setExpanded] = useState({});
  const { requestConfirmation, dialog: confirmDialog } = useAppConfirm();

  /* Detect which starter (if any) the current label set matches exactly. */
  const matchedStarterId = (() => {
    for (const t of PIECEWISE_STARTER_TEMPLATES) {
      const all = Object.keys(t.labels).every((k) => labels[k] === t.labels[k]);
      if (all) return t.id;
    }
    return null;
  })();

  const applyTemplate = (template) => {
    setScenario({ ...scenario, piecewiseLabels: { ...template.labels } });
  };

  const setLabel = (kind, value) => {
    setScenario({
      ...scenario,
      piecewiseLabels: { ...labels, [kind]: value },
    });
  };

  const resetLabels = async () => {
    const ok = await requestConfirmation({
      title: 'Reset piecewise labels?',
      message: 'This resets labels to the universal model defaults. Schedule data and overrides are preserved.',
      confirmLabel: 'Reset labels',
      tone: 'danger',
    });
    if (!ok) return;
    setScenario((current) => ({ ...current, piecewiseLabels: { ...DEFAULT_PIECEWISE_LABELS } }));
  };

  const setOverride = (key, schedule) => {
    const overrides = { ...(scenario.piecewiseOverrides ?? {}) };
    if (schedule == null) delete overrides[key];
    else overrides[key] = schedule;
    setScenario({ ...scenario, piecewiseOverrides: overrides });
  };

  const toggleExpand = (id) => {
    setExpanded((e) => ({ ...e, [id]: !e[id] }));
  };

  return (
    <div>
      {/* Starter template bar */}
      <div className={STARTER_BAR}>
        <span className="text-[0.78rem] text-text-muted mr-1">Model starter:</span>
        {PIECEWISE_STARTER_TEMPLATES.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`${STARTER_BTN} ${matchedStarterId === t.id ? STARTER_BTN_ACTIVE : ''}`}
            onClick={() => applyTemplate(t)}
            title={t.description}
          >
            {t.name}
          </button>
        ))}
        <button
          type="button"
          className={`${RESET_BTN} ml-auto`}
          onClick={resetLabels}
          title="Reset all labels to universal model defaults (does not affect schedule data)"
        >
          Reset labels
        </button>
      </div>
      <p className={HINT}>
        Templates change only the <strong className="text-text">labels</strong> shown in the editor — the underlying schedules,
        overrides, and engine math are preserved. Refine each label inline to match your finance, revenue, cost, expense, operations, asset, and liability model.
      </p>

      {/* Per-kind sections */}
      {KINDS.map((kind) => {
        const isOpen = !!expanded[kind.id];
        return (
          <section key={kind.id} className={SECTION}>
            <div className={SECTION_HEAD}>
              <div className="flex flex-col gap-[2px] flex-1 min-w-[260px]">
                <input
                  type="text"
                  className={LABEL_INPUT}
                  value={labels[kind.id] ?? ''}
                  onChange={(e) => setLabel(kind.id, e.target.value)}
                  placeholder={`Label for ${kind.id}`}
                  aria-label={`Label for ${kind.id}`}
                />
                <input
                  type="text"
                  className={HINT_INPUT}
                  value={hints[kind.id] ?? ''}
                  readOnly
                  placeholder={kind.description}
                />
              </div>
              <button
                type="button"
                className={STARTER_BTN}
                onClick={() => toggleExpand(kind.id)}
                aria-expanded={isOpen}
              >
                {isOpen ? '▾ Collapse schedules' : '▸ Edit schedules'}
              </button>
            </div>
            {isOpen && (
              <div className="pt-[6px]">
                {kind.renderBody({ scenario, setScenario, setOverride })}
              </div>
            )}
          </section>
        );
      })}

      <p className={HINT}>
        Engine pathways are unchanged — labels just change how the four built-in piecewise kinds are surfaced. When the engine learns to honor user-added kinds (Phase 2), this same screen will let you add new categories beyond the four built-ins.
      </p>
      {confirmDialog}
    </div>
  );
}
